import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'Components/Base';
import VideoModal from './VideoModal';
import LibraryListModal from './LibraryListModal';
import VideoListItem from './VideoListItem';
import VideoTemplateDispatcher from 'Api/Dispatchers/VideoTemplateDispatcher';

import './VideoSection.scss';

const VideoSection = ({
  parentId,
  uploadedVideos = [],
  libraryVideos = [],
  isTemplate = false,
  allTags = [],
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [isLibraryListOpen, setIsLibraryListOpen] = useState(false);
  const [recordsProcessing, setRecordsProcessing] = useState({});
  const [newlyAddedVideos, setNewlyAddedVideos] = useState([]);
  const [deletedVideoIds, setDeletedVideoIds] = useState(new Set());
  const [updatedVideos, setUpdatedVideos] = useState({});


  const allVideos = useMemo(() => {
    const uploaded = uploadedVideos
      .filter(video => !deletedVideoIds.has(video.id))
      .map(video => updatedVideos[video.id] ? { ...video, ...updatedVideos[video.id] } : video);
    const newlyAdded = newlyAddedVideos
      .filter(video => !deletedVideoIds.has(video.id))
      .map(video => updatedVideos[video.id] ? { ...video, ...updatedVideos[video.id] } : video);
    return [...uploaded, ...newlyAdded];
  }, [uploadedVideos, newlyAddedVideos, deletedVideoIds, updatedVideos]);

  const filteredVideos = useMemo(() => {
    if (!searchInput.trim()) return allVideos;
    const lowerSearch = searchInput.toLowerCase();
    return allVideos.filter((video) => video.name.toLowerCase().includes(lowerSearch));
  }, [allVideos, searchInput]);

  const handleInputChange = useCallback((evt) => {
    setSearchInput(evt.target.value);
  }, []);

  const handleOpenAddVideoModal = useCallback(() => {
    setEditingVideo(null);
    setIsVideoModalOpen(true);
  }, []);

  const handleOpenEditVideoModal = useCallback((video) => {
    setEditingVideo(video);
    setIsVideoModalOpen(true);
  }, []);

  const handleOpenLibraryList = useCallback(() => {
    setIsLibraryListOpen(true);
  }, []);

  const handleCloseVideoModal = useCallback(() => {
    setIsVideoModalOpen(false);
    setEditingVideo(null);
  }, []);

  const handleCloseLibraryList = useCallback(() => {
    setIsLibraryListOpen(false);
  }, []);

  const handleLibraryVideoSubmit = useCallback(async (selectedVideoIds) => {
    const api = new VideoTemplateDispatcher(parentId, isTemplate);

    const promises = selectedVideoIds.map(async (videoId) => {
      const formData = {
        video: {
          library_video_id: videoId,
        }
      };

      try {
        const response = await api.create(formData);
        if (response.ok) {
          const newVideo = await response.json();
          return { success: true, video: newVideo };
        } else if (response.status === 422) {
          const error = await response.json();
          console.error(error);
          return { success: false, error };
        }
      } catch (error) {
        console.error('Error adding library video:', error);
        return { success: false, error };
      }
    });

    const results = await Promise.all(promises);
    const successfulVideos = results
      .filter(result => result.success)
      .map(result => result.video);

    if (successfulVideos.length > 0) {
      setNewlyAddedVideos((prev) => [...prev, ...successfulVideos]);
    }
  }, [parentId, isTemplate]);

  const handleRemoveVideoFromState = useCallback((videoToRemove) => {
    setDeletedVideoIds((prev) => new Set(prev).add(videoToRemove.id));

    setNewlyAddedVideos((prev) => prev.filter((video) => video.id !== videoToRemove.id));
  }, []);

  const setRecordsProcessingState = useCallback((video, isProcessing) => {
    setRecordsProcessing((prev) => ({ ...prev, [video.id]: isProcessing }));
  }, []);

  const dispatchRemoveVideo = useCallback(async (videoToRemove) => {
    setRecordsProcessingState(videoToRemove, true);

    try {
      const api = new VideoTemplateDispatcher(parentId, isTemplate, videoToRemove.id);
      const response = await api.destroy();
      if (response.ok) {
        handleRemoveVideoFromState(videoToRemove);
      } else if (response.status === 422) {
        response.json().then(console.error);
      }
    } catch (error) {
      console.error('Error removing video:', error);
    } finally {
      setRecordsProcessingState(videoToRemove, false);
    }
  }, [handleRemoveVideoFromState, setRecordsProcessingState, parentId, isTemplate]);

  const handleSubmit = useCallback(async (videoData) => {
    const isEditing = !!editingVideo;
    const api = new VideoTemplateDispatcher(parentId, isTemplate, editingVideo?.id);

    const formData = {
      video: {
        name: videoData.name,
        description: videoData.description,
        video_url: videoData.url,
        tags: videoData.tags,
      }
    };

    const response = isEditing
      ? await api.update(formData)
      : await api.create(formData);

    if (response.ok) {
      const updatedVideo = await response.json();

      if (isEditing) {
        setUpdatedVideos((prev) => ({
          ...prev,
          [updatedVideo.id]: updatedVideo
        }));

        setNewlyAddedVideos((prev) =>
          prev.map(video => video.id === updatedVideo.id ? updatedVideo : video)
        );
      } else {
        setNewlyAddedVideos((prev) => [...prev, updatedVideo]);
      }

      handleCloseVideoModal();
    } else if (response.status === 422) {
      response.json().then(console.error);
    }
  }, [parentId, isTemplate, editingVideo, handleCloseVideoModal]);

  return (
    <div className="video-section">
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={handleCloseVideoModal}
        onSubmit={handleSubmit}
        isEdit={!!editingVideo}
        video={editingVideo}
        allTags={allTags}
      />
      <LibraryListModal
        isOpen={isLibraryListOpen}
        onClose={handleCloseLibraryList}
        onSubmit={handleLibraryVideoSubmit}
        libraryVideos={libraryVideos.filter(video => {
          const allAddedVideos = [...uploadedVideos, ...newlyAddedVideos];
          return !allAddedVideos.some(v => v.libraryVideoId === video.id);
        })}
      />

      <div className="video-section__buttons">
        <Button
          text="Video Library"
          className="btn btn--primary"
          onClick={handleOpenLibraryList}
        />
        <Button
          text="Add Video"
          className="btn btn--primary"
          onClick={handleOpenAddVideoModal}
        />
      </div>

      <div className="video-section__search">
        <input
          type="text"
          placeholder="Search videos..."
          className="form-control"
          value={searchInput}
          onChange={handleInputChange}
        />
      </div>

      <div className="video-section__content">
        {filteredVideos.length === 0 ? (
          <p>No videos added yet.</p>
        ) : (
          <ul className="video-list">
            {filteredVideos.map((video) => (
              <VideoListItem
                key={video.id}
                video={video}
                isProcessing={!!recordsProcessing[video.id]}
                onEdit={handleOpenEditVideoModal}
                onRemove={dispatchRemoveVideo}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

VideoSection.propTypes = {
  parentId: PropTypes.number.isRequired,
  selectedLibraryVideos: PropTypes.arrayOf(PropTypes.string),
  uploadedVideos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  libraryVideos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      status: PropTypes.string,
      tags: PropTypes.array,
    })
  ),
  isTemplate: PropTypes.bool,
  allTags: PropTypes.array,
};

export default VideoSection;
